import Image from "next/image";
import Link from "next/link";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { getTemplateStyles } from "@/lib/siteTemplateStyles";
import { AudienceSection } from "@/components/blocks/AudienceSection";

export function ImageTextSplitBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  return (
    <AudienceSection content={content}>
      {(resolved) => <ImageTextSplitInner content={resolved} template={template} />}
    </AudienceSection>
  );
}

function ImageTextSplitInner({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "");
  const body = String(content.body ?? "");
  const imageUrl = String(content.imageUrl ?? "");
  const imageAlt = String(content.imageAlt ?? "");
  const imagePosition = String(content.imagePosition ?? "left") as "left" | "right";
  const objectPosition = String(content.objectPosition ?? "center center");
  const buttonText = content.buttonText ? String(content.buttonText) : null;
  const buttonUrl = content.buttonUrl ? String(content.buttonUrl) : null;
  const styles = getTemplateStyles(template);

  if (!heading && !body && !imageUrl) return null;

  const imageSide = (
    <div className="relative min-h-[16rem] overflow-hidden rounded-[var(--border-radius)]">
      {imageUrl ? (
        <Image
          src={imageUrl}
          alt={imageAlt}
          fill
          className="object-cover"
          style={{ objectPosition }}
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      ) : (
        <div className="h-full w-full bg-[var(--color-surface-muted)]" />
      )}
    </div>
  );

  const textSide = (
    <div className="flex flex-col justify-center gap-[calc(var(--space-unit)*0.75)]">
      {heading && (
        <h2
          className="font-bold text-[var(--color-text)]"
          style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)", lineHeight: "var(--line-heading)" }}
        >
          {heading}
        </h2>
      )}
      {body && (
        <div
          className="prose prose-sm max-w-none text-[var(--color-secondary)]"
          dangerouslySetInnerHTML={{ __html: body }}
        />
      )}
      {buttonText && buttonUrl && (
        <Link
          href={buttonUrl}
          className="mt-2 inline-flex min-h-[44px] w-fit items-center justify-center rounded-[var(--border-radius)] bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          {buttonText}
        </Link>
      )}
    </div>
  );

  return (
    <section className={styles.textGroup.section}>
      <div
        className={`grid grid-cols-1 items-center gap-[calc(var(--space-unit)*2)] md:grid-cols-2 ${
          imagePosition === "right" ? "md:[direction:rtl]" : ""
        }`}
      >
        <div className={imagePosition === "right" ? "md:[direction:ltr]" : ""}>{imageSide}</div>
        <div className={imagePosition === "right" ? "md:[direction:ltr]" : ""}>{textSide}</div>
      </div>
    </section>
  );
}
